import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Badge, Spinner } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { IoMdAddCircle } from "react-icons/io";
import { useSessionStorage } from "react-use";
import FinancialOrderEnum from "../../../../enums/FinancialOrderEnum";
import { getByAuthService } from "../../../../function/helper";
import style from "./ShipmentSection.module.css";
import DetailBox from "./components/DetailBox";
import ShipmentForm from "./components/ShipmentForm";
import detectDiryFields from "./functions/detectDiryFields";
import generateRandomString from "./functions/generateRandomString";

function ShipmentSectionVip({
  lastPartNumber = 100,
  company,
  senderAddress,
  hasFinancialOrder,
}) {
  const [formValues, setFormValues] = useState({
    count: 0,
    values: null,
  });

  const baseInfoShipment = {
    packaging: 0,
    cost: 10000000,
    isFragile: false,
    isLiquid: false,
    ui: generateRandomString(),
    serviceInPlace: 0,
    needPackaging: false,
    content: [{ key: generateRandomString() }],
  };

  const [vipTariff, setVipTariff] = useState({
    dayCount: -1,
    monthRemain: -1,
  });

  const { isLoading } = useQuery({
    queryKey: ["monthly-commitment"],
    queryFn: () =>
      getByAuthService("/totalpost/v1/vip/getRemainSpecialOrderVIP").then(
        res => {
          if (res.status === 200) {
            setVipTariff(res.data.result);
          }
          return 0;
        }
      ),
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: hasFinancialOrder(FinancialOrderEnum.CountPrice),
  });

  const [shipments, setShipments] = useSessionStorage("shipmentsVIP", [
    baseInfoShipment,
  ]);

  const [prices, setPrices] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    control,
    getValues,
    watch,
    reset,
    setValue,
    unregister,
  } = useForm();

  const { fields, remove, insert, append } = useFieldArray({
    name: "items",
    control,
  });

  useEffect(() => {
    shipments.forEach(item => append(item, { shouldFocus: false }));
  }, []);

  useEffect(() => {
    setPrices([]);
  }, [company, senderAddress]);

  useEffect(() => {
    setShipments(getValues("items"));
  }, [getValues()]);

  return (
    <section className="pt-3">
      <div className="d-flex justify-content-between gap-3 mb-3">
        <p className="title-style ">مشخصات مرسوله</p>

        {hasFinancialOrder(FinancialOrderEnum.CountPrice) && (
          <Badge
            style={{
              fontSize: "1rem",
              padding: "0.75rem 0.5rem",
              backgroundColor: "#6f42c1",
            }}
            className="d-flex align-items-center justify-content-center gap-3"
          >
            <p className="m-0">مرسولات باقی مانده تا تعهد ماهانه: </p>
            {isLoading ? (
              <Spinner />
            ) : (
              <strong>
                {Math.max(
                  vipTariff.monthRemain - getValues("items")?.length,
                  0
                )}
              </strong>
            )}
          </Badge>
        )}
      </div>

      <button
        className={"buttonUi rounded-1 f-center " + style.addBtn}
        onClick={() => append(baseInfoShipment)}
      >
        <span className="ms-3">افزودن</span>
        <span className="fs-4">
          <IoMdAddCircle />
        </span>
      </button>

      <form
        id="formVip"
        onKeyDown={e => {
          if (e.key === "Enter" && e.target.nodeName === "INPUT") {
            const form = e.target.form;
            if (form?.elements === undefined) return;

            const index = Array.prototype.indexOf.call(form.elements, e.target);
            if (form.elements[index + 1]) {
              form.elements[index + 1].focus();
            }
            e.preventDefault();
          }
        }}
        onSubmit={handleSubmit(values =>
          setFormValues(cur => ({ count: cur.count + 1, values }))
        )}
      >
        {fields.map((item, idx) => (
          <ShipmentForm
            key={item.ui}
            index={idx}
            register={register}
            shipment={item}
            errors={errors?.items ? errors?.items[idx] : {}}
            remove={remove}
            getValues={getValues}
            insert={insert}
            lastPartNumber={lastPartNumber}
            watch={watch}
            control={control}
            setValue={setValue}
            isDirty={detectDiryFields(dirtyFields, idx)}
            unregister={unregister}
            priceState={{ prices, setPrices }}
            vipTariff={vipTariff}
            hasFinancialOrder={hasFinancialOrder}
          />
        ))}

        {hasFinancialOrder(FinancialOrderEnum.CountPrice) && (
          <div className="d-flex justify-content-end gap-3 mb-3">
            <Badge
              style={{ fontSize: "1rem", padding: "0.75rem 0.5rem" }}
              className="d-flex align-items-center justify-content-center gap-3 bg-avaex"
            >
              <p className="m-0">تعداد مرسولات امروز: </p>
              <strong>
                {vipTariff.dayCount + (getValues("items")?.length ?? 0)}
              </strong>
            </Badge>
          </div>
        )}

        <DetailBox
          shipments={getValues("items") ?? []}
          reset={reset}
          formValues={formValues}
          prices={prices}
          setPrices={setPrices}
          hasFinancialOrder={hasFinancialOrder}
        />
      </form>
    </section>
  );
}

export default ShipmentSectionVip;
